import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import { fileSvc } from '../../utils/FileSvc'
import { fileUploadSvc } from '../../utils/FileUploadSvc'
import { handleJSAPIAccess } from '../../utils/auth_access_util'
import { showToast } from '../../utils/messageUtil';
import { useUser } from '../../contexts/UserContext'
import './index.css'

// 缓存管理函数已从 auth_access_util.js 导入

export default function CarReturnApproval() {

    const navigate = useNavigate()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        const hasName = userInfo.name && userInfo.name.trim() !== ''
        const hasMobile = userInfo.mobile && userInfo.mobile.trim() !== ''
        const hasDepartment = userInfo.department || userInfo.department_name
        return hasUserId && hasName && hasMobile && hasDepartment
    }

    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('还车审批页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    const [approvals, setApprovals] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    // 筛选条件
    const [filters, setFilters] = useState({
        plateNumber: ''
    })

    // 弹窗状态
    const [approvalModalVisible, setApprovalModalVisible] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState(null)
    const [approvalOpinion, setApprovalOpinion] = useState('')
    const [submitting, setSubmitting] = useState(false)
    // 可编辑的用车总里程
    const [editTotalMileage, setEditTotalMileage] = useState('')

    const [confirmImages, setConfirmImages] = useState([])
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    const containerRef = useRef(null)

    // 获取审批数据
    const fetchApprovals = async (page = 1) => {
        try {
            if (page === 1) {
                setLoading(true)
            } else {
                setLoadingMore(true)
            }
            setError(null)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getCarReturnList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                page: page,
                rows: 10,
                ...filters
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                const approvalData = response.data.result.rows || []
                const total = response.data.result.total || 0
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)

                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)

                if (page === 1) {
                    setApprovals(approvalData)
                } else {
                    setApprovals(prevApprovals => [...prevApprovals, ...approvalData])
                }
            } else {
                if (page === 1) {
                    setApprovals([])
                }
                setError('获取审批数据失败')
            }
        } catch (error) {
            console.error('获取审批数据失败:', error)
            if (page === 1) {
                setApprovals([])
            }
            setError('网络错误，请稍后重试')
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 初始化数据
    useEffect(() => {
        fetchApprovals(1)
    }, [filters])



    // 加载更多
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            const nextPage = currentPage + 1
            fetchApprovals(nextPage)
        }
    }, [loadingMore, hasMore, currentPage, filters])

    // 上划到底部加载更多
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return

            const { scrollTop, scrollHeight, clientHeight } = container
            // 当滚动到底部附近时触发加载更多
            if (scrollHeight - scrollTop - clientHeight < 50) {
                handleLoadMore()
            }
        }

        const container = containerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [loadingMore, hasMore, handleLoadMore])

    // 处理筛选条件变化
    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // 进入审批详情（打开弹窗）
    const handleApprovalDetail = (approval) => {
        setSelectedApproval(approval)
        setApprovalOpinion('')
        setConfirmImages([])
        setUploadProgress(0)
        // 初始化可编辑的里程数为原始值
        setEditTotalMileage(approval.totalMileage?.toString() || '')
        setApprovalModalVisible(true)
    }

    // 处理审批意见变化
    const handleApprovalOpinionChange = (e) => {
        setApprovalOpinion(e.target.value)
    }

    // 提交审批
    const submitApproval = async (approvalStatus) => {
        if (!selectedApproval) return

        // 校验用车里程不能为空
        if (!editTotalMileage || editTotalMileage.trim() === '') {
            showToast('用车里程不能为空', 'warning');
            return
        }
        
        // 校验里程数不能为负数
        const mileage = parseFloat(editTotalMileage);
        if (isNaN(mileage) || mileage < 0) {
            showToast('用车里程不能为负数', 'warning');
            return
        }

        if (approvalStatus === '2' && !approvalOpinion.trim()) {
            showToast('驳回时审批意见必填', 'warning');
            return
        }

        try {
            setSubmitting(true)
            setError(null)

            let confirmImagesStr = '';
            if (confirmImages.length > 0) {
                const allUploaded = confirmImages.every(img => img.uploaded);
                if (!allUploaded) {
                    showToast('请等待所有确认图片上传完成', 'error');
                    setSubmitting(false);
                    return;
                }
                confirmImagesStr = confirmImages.map(img => img.url).join(',');
            }

            // 获取修改后的里程数，若无修改则使用原始值
            const mileage = editTotalMileage !== '' ? parseFloat(editTotalMileage) : selectedApproval.totalMileage;
            
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicationId: selectedApproval.applicationId,
                applicantId: selectedApproval.applicantId || '',
                plateNumber: selectedApproval.plateNumber,
                returnApproverId: userInfo?.user_id || '',
                returnApproverName: userInfo?.name || '',
                approvalStatus: approvalStatus === '1' ? '4' : '5',
                returnApprovalOpinion: approvalOpinion || (approvalStatus === '1' ? '同意' : '驳回'),
                confirmImages: confirmImagesStr,
                totalMileage: mileage
            }

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/return/approve`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('审批成功', 'success');
                setApprovalModalVisible(false)
                fetchApprovals(1)
            } else {
                showToast('审批失败:' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('审批失败:', error)
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            setSubmitting(false)
        }
    }

    // 关闭弹窗
    const handleCloseModal = () => {
        setApprovalModalVisible(false)
        setSelectedApproval(null)
        setApprovalOpinion('')
        setConfirmImages([])
        setUploadProgress(0)
    }
    // 选择确认图片
    const chooseConfirmImages = () => {
        console.log('开始选择确认图片');

        const isPC = navigator.userAgent.includes('Windows') || navigator.userAgent.includes('Macintosh');
        const sourceType = isPC ? ['album'] : ['album', 'camera'];

        const handleChooseImage = () => {
            if (typeof fileSvc !== 'undefined' && fileSvc.chooseImage) {
                fileSvc.chooseImage({
                    count: 9,
                    sourceType: sourceType,
                    sizeType: ['compressed']
                }).subscribe(
                    (files) => {
                        console.log('选择确认图片成功:', files);
                        const newImages = files.map(file => ({
                            path: file.path,
                            size: file.size,
                            uploaded: false,
                            url: '',
                            thumbnail: file.thumbnail || file.path
                        }));
                        setConfirmImages(prev => [...prev, ...newImages]);
                    },
                    (error) => {
                        console.log('选择确认图片失败:', error);
                        const errorMessage = error.message || '';
                        if (errorMessage.includes('cancel') || errorMessage.includes('用户取消')) {
                            console.log('用户取消选择图片');
                            return;
                        }
                        if (isPC) {
                            handleNativeFileSelect();
                        } else {
                            showToast(`选择图片失败: ${error.message}`, 'error');
                        }
                    }
                );
            } else {
                handleNativeFileSelect();
            }
        };

        if (window.h5sdk) {
            console.log('飞书环境，先进行 JSAPI 鉴权');
            handleJSAPIAccess((isSuccess) => {
                if (isSuccess) {
                    console.log('JSAPI 鉴权成功，开始选择图片');
                    handleChooseImage();
                } else {
                    console.log('JSAPI 鉴权失败，使用原生文件选择');
                    handleNativeFileSelect();
                }
            });
        } else {
            console.log('非飞书环境，使用原生文件选择');
            handleNativeFileSelect();
        }
    }
    // 使用原生文件选择
    const handleNativeFileSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.max = 9;
        input.style.display = 'none';

        input.onchange = (e) => {
            const files = Array.from(e.target.files);
            if (files.length > 0) {
                console.log('原生文件选择成功:', files);
                const newImages = files.map(file => ({
                    path: URL.createObjectURL(file),
                    size: file.size,
                    uploaded: false,
                    url: '',
                    thumbnail: URL.createObjectURL(file),
                    file: file
                }));
                setConfirmImages(prev => [...prev, ...newImages]);
            }
        };

        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }

    // 删除确认图片
    const removeConfirmImage = (index) => {
        setConfirmImages(prev => prev.filter((_, i) => i !== index));
    }

    // 上传确认图片到阿里OSS
    const uploadConfirmImages = async () => {
        if (confirmImages.length === 0) {
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            const files = confirmImages.map(img => {
                if (img.file) {
                    return {
                        path: img.path,
                        size: img.size,
                        name: img.file.name || `confirm_image_${Date.now()}.jpg`
                    };
                } else {
                    return {
                        path: img.path,
                        size: img.size,
                        name: `confirm_image_${Date.now()}.${img.path.split('.').pop()}`
                    };
                }
            });

            let totalProgress = 0;
            const uploadPromises = files.map((file, index) => {
                return fileUploadSvc.upload(file, 'car_return_confirm', {}, (progress) => {
                    const currentProgress = Math.round(((index + progress / 100) / files.length) * 100);
                    if (currentProgress > totalProgress) {
                        totalProgress = currentProgress;
                        setUploadProgress(totalProgress);
                    }
                });
            });

            const uploadedUrls = await Promise.all(uploadPromises);

            const uploadedImages = confirmImages.map((img, index) => ({
                ...img,
                uploaded: true,
                url: uploadedUrls[index],
                thumbnail: uploadedUrls[index]
            }));
            setConfirmImages(uploadedImages);
            showToast('图片上传成功', 'success');
        } catch (error) {
            console.error('上传确认图片失败:', error);
            showToast('上传图片失败，请重试', 'error');
        } finally {
            setUploading(false);
        }
    }
    // 返回首页
    const handleBack = () => {
        navigate('/')
    }

    return (
        <div
            className="car-return-approval-page"
            ref={containerRef}
        >
            <h2>还车审批</h2>

            {/* 筛选条件 */}
            <div className="filter-container">
                    <div className="filter-item search-input">
                        <input
                            type="text"
                            name="plateNumber"
                            value={filters.plateNumber}
                            onChange={handleFilterChange}
                            placeholder="请输入车牌号码"
                        />
                </div>
            </div>

            {loading ? (
                <div className="loading-indicator">
                    <p>正在加载审批数据...</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button className="retry-btn" onClick={() => fetchApprovals(1)}>重试</button>
                </div>
            ) : (
                <div className="approvals-list">
                    {approvals.length === 0 ? (
                        <div className="no-approvals">
                            <p>暂无审批数据</p>
                        </div>
                    ) : (
                        <>
                            <div className="approval-cards">
                                {approvals.map(approval => (
                                    <div key={approval.applicationId} className="approval-card">
                                        <div className="card-header">
                                            <h3>{approval.plateNumber}</h3>
                                            <span className="application-code">{approval.applicationNo}</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="card-info">
                                                <div className="info-item">
                                                    <span className="label">申请人:</span>
                                                    <span className="value">{approval.applicantName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">申请人电话:</span>
                                                    <span className="value">{approval.applicantPhone}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">所属部门:</span>
                                                    <span className="value">{approval.applicantDept}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">驾驶方式:</span>
                                                    <span className="value">{approval.driveType === 1 ? '自驾' : '司机代驾'}</span>
                                                </div>
                                                {approval.driveType === 2 && (
                                                    <div className="info-item">
                                                        <span className="label">代驾司机:</span>
                                                        <span className="value">{approval.driverName}</span>
                                                    </div>
                                                )}
                                                <div className="info-item">
                                                    <span className="label">申请类型:</span>
                                                    <span className="value">
                                                        {approval.isPersonal === 0 ? '本人申请' : '代人申请'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">用车人:</span>
                                                    <span className="value">{approval.vehicleUserName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">使用场景:</span>
                                                    <span className="value">
                                                        {approval.usageScenarios === 1 ? '员工外出' :
                                                            approval.usageScenarios === 2 ? '一般客户接待' :
                                                                approval.usageScenarios === 3 ? '重要客户接待' :
                                                                    approval.usageScenarios}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">还车时间:</span>
                                                    <span className="value">{approval.returnApplyTime}</span>
                                                </div>
                                                <div className="info-item full-width">
                                                    <span className="label">用车时间:</span>
                                                    <span className="value">{approval.expectedStartTime} 至 {approval.expectedEndTime}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">出发地:</span>
                                                    <span className="value">{approval.expectedLocation}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">目的地:</span>
                                                    <span className="value">{approval.destination}</span>
                                                </div>
                                                {approval.passengers && approval.passengers.trim() !== '' && (
                                                    <div className="info-item">
                                                        <span className="label">同行人:</span>
                                                        <span className="value">{approval.passengers}</span>
                                                    </div>
                                                )}
                                                <div className="info-item">
                                                    <span className="label">开始总里程:</span>
                                                    <span className="value">{approval.startTotalMileage || 0} km</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">结束总里程:</span>
                                                    <span className="value">{approval.endTotalMileage || 0} km</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">用车总里程:</span>
                                                    <span className="value">{approval.totalMileage || 0} km</span>
                                                </div>
                                                <div className="info-item full-width">
                                                    <span className="label">用车原因:</span>
                                                    <span className="value">{approval.reason}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <button
                                                onClick={() => handleApprovalDetail(approval)}
                                                style={{
                                                    padding: '12px 20px',
                                                    backgroundColor: '#1890ff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    height: '40px'
                                                }}
                                            >
                                                还车审批
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingMore ? (
                                <div className="loading-more">
                                    <p>加载中...</p>
                                </div>
                            ) : hasMore ? (
                                <div className="load-more-container">
                                    <button className="load-more-btn" onClick={handleLoadMore}>
                                        加载更多
                                    </button>
                                </div>
                            ) : (
                                <div className="no-more-data">
                                    <p>没有更多数据了</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            <div className="action-buttons">
                <button className="back-btn" onClick={handleBack}>返回首页</button>
            </div>

            {/* 审批详情弹窗 */}
            {approvalModalVisible && selectedApproval && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            还车审批_{selectedApproval.plateNumber}
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请单号:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicationNo}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请人姓名:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicantName}</span>
                            </div>
                            {/*      <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>所属部门:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicantDept}</span>
                            </div>*/}
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px'
                            }}>
                                <span style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#666'
                                }}>用车时间:</span>
                                <span style={{
                                    display: 'block',
                                    color: '#333'
                                }}>{selectedApproval.expectedStartTime} 至 {selectedApproval.expectedEndTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>还车时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.returnApplyTime}</span>
                            </div>
                            {/*            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>使用场景:</span>
                                <span style={{
                                    color: '#333'
                                }}>
                                    {selectedApproval.usageScenarios === 1 ? '员工外出' : 
                                     selectedApproval.usageScenarios === 2 ? '一般客户接待' : 
                                     selectedApproval.usageScenarios === 3 ? '重要客户接待' : 
                                     selectedApproval.usageScenarios}
                                </span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>驾驶方式:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.driveType === 1 ? '自驾' : '司机代驾'}</span>
                            </div>*/}
                            {/* <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>出发地:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.expectedLocation}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>目的地:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.destination}</span>
                            </div>*/}
                            {/*        <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>开始总里程:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.startTotalMileage || 0} km</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>结束总里程:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.endTotalMileage || 0} km</span>
                            </div>*/}
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车总里程:</span>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={editTotalMileage}
                                    onChange={(e) => setEditTotalMileage(e.target.value)}
                                    placeholder="请输入里程数"
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                                <span style={{
                                    color: '#333'
                                }}>km</span>
                            </div>
                            {/*             <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车原因:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.reason}</span>
                            </div>*/}

                            {selectedApproval.returnImages && typeof selectedApproval.returnImages === 'string' && selectedApproval.returnImages.trim() !== '' && (
                                <div style={{
                                    backgroundColor: '#f9f9f9',
                                    padding: '15px',
                                    borderRadius: '8px'
                                }}>
                                    <span style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 'bold',
                                        color: '#666'
                                    }}>还车图片:</span>
                                    <div style={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {(() => {
                                            const images = selectedApproval.returnImages.split(',').map(img => img.trim()).filter(img => img);

                                            return images.map((imageUrl, index) => (
                                                <div key={index} style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer'
                                                }} onClick={() => {
                                                    fileSvc.previewImage(images, imageUrl)
                                                        .then(() => console.log('预览图片成功'))
                                                        .catch(error => console.log('预览图片失败:', error));
                                                }}>
                                                    <img
                                                        src={imageUrl}
                                                        alt={`还车图片 ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* 确认图片上传区域 */}
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px'
                            }}>
                                <span style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontWeight: 'bold',
                                    color: '#666'
                                }}>确认图片: <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#999' }}>(非必填)</span></span>

                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '10px',
                                    marginBottom: '15px'
                                }}>
                                    {confirmImages.map((image, index) => (
                                        <div key={index} style={{
                                            position: 'relative',
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                            border: '1px solid #ddd',
                                            cursor: 'pointer'
                                        }} onClick={() => {
                                            const imageUrls = confirmImages.map(img => img.uploaded ? img.url : img.path);
                                            fileSvc.previewImage(imageUrls, image.uploaded ? image.url : image.path)
                                                .then(() => console.log('预览图片成功'))
                                                .catch(error => console.log('预览图片失败:', error));
                                        }}>
                                            <img
                                                src={image.thumbnail || image.path}
                                                alt={`确认图片 ${index + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeConfirmImage(index);
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                ×
                                            </button>
                                            {image.uploaded && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    textAlign: 'center'
                                                }}>
                                                    已上传
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <button
                                        onClick={chooseConfirmImages}
                                        style={{
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '4px',
                                            border: '2px dashed #ddd',
                                            backgroundColor: '#f9f9f9',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            color: '#999'
                                        }}
                                    >
                                        <span style={{ fontSize: '24px' }}>+</span>
                                        <span>添加图片</span>
                                    </button>
                                </div>

                                {confirmImages.length > 0 && (
                                    <button
                                        onClick={uploadConfirmImages}
                                        disabled={uploading || confirmImages.length === 0}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#007AFF',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: uploading || confirmImages.length === 0 ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            opacity: uploading || confirmImages.length === 0 ? 0.6 : 1
                                        }}
                                    >
                                        {uploading ? '上传中...' : '上传图片'}
                                    </button>
                                )}

                                {uploading && uploadProgress > 0 && (
                                    <div style={{
                                        marginTop: '10px',
                                        height: '6px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '3px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            backgroundColor: '#007AFF',
                                            width: `${uploadProgress}%`,
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                )}
                            </div>
                        </div>


                        <div style={{
                            marginBottom: '30px'
                        }}>
                            <span style={{
                                display: 'block',
                                marginBottom: '10px',
                                fontWeight: 'bold',
                                color: '#666'
                            }}>审批意见:</span>
                            <textarea
                                value={approvalOpinion}
                                onChange={handleApprovalOpinionChange}
                                placeholder="请输入审批意见（选填）"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    minHeight: '100px'
                                }}
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                            marginBottom: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={handleCloseModal}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={() => submitApproval('2')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                驳回
                            </button>
                            <button
                                onClick={() => submitApproval('1')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                通过
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}